import styles from "./OpportunityForm.module.scss";
import Select from "react-select";
import { useEffect, useRef, useState } from "react";
import useExternalApiService from "helpers/ExternalApiService";
import { ReactComponent as PlusIcon } from "assets/plus.svg";
import { ReactComponent as BackIcon } from "assets/blackBack.svg";
import { ReactComponent as LockIcon } from "assets/LockSimple.svg";
import { store } from "stores/Store";
import ProductForm from "./ProductForm";
import ProductList from "./ProductList";
import { useNavigate } from "react-router-dom";
import { formatMoney } from "helpers";
import { Box, Button, Group, Overlay, ScrollArea, Stack, Title } from "@mantine/core";
import { getFullDateUSStyle, getNumberRemaingMonthsThisYear } from "helpers/date";
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker-cssmodules.css'

// type View = "submit" | "draft" | "lead";

interface SalesStage {
  label: string;
  description: string;
  percentage: number;
}

const SALES_STAGES: SalesStage[] = [{
  label: "Prospect (Sales Qualified)",
  description: "Qualified Lead or Existing Customer",
  percentage: 0.1,
}, {
  label: "Prepare/Qualify",
  description: "Information Gathering and Understanding Prospect",
  percentage: 0.2,
}, {
  label: "Present",
  description: "Personalized, Tailored Solutions Presented to Prospect",
  percentage: 0.6,
}, {
  label: "Follow-Up",
  description: "Overcome Objections, Address Concerns, Ask for Sale",
  percentage: 0.7,
}, {
  label: "Closed Won",
  description: "Order placed",
  percentage: 1,
}, {
  label: "Closed Lost",
  description: "Customer not moving forward with opportunity",
  percentage: 0,
}
];

const RISK_STAGES: SalesStage[] = [{
  label: "At Risk",
  description: "",
  percentage: 0.5,
}, {
  label: "Lost Business",
  description: "",
  percentage: 1,
}, {
  label: "Retained",
  description: "",
  percentage: 0,
}]

const WIN_LOSS_REASONS = {
  win: [
    "WON - Business Solutions",
    "WON - Clinical Advantage",
    "WON - Competitive Advantage/Other",
    "WON - Competitive Pricing",
  ],
  loss: [
    "LOST - Budget Constraints",
    "LOST - Lack of Product Fit",
    "LOST - Lost to Competitors",
    "LOST - Service Level/Other",
  ],
};

const STATUSES_WEIGHTS: { [k: string]: number } = {
  "Sales Ready": 0.1,
  "Prepare/Quality": 0.2,
  "Present": 0.6,
  "Follow-up": 0.7,
  "Closed Lost": 0,
  "Verified": 0,
  "Closed Won": 1,
};

// Using ths to make display more friendly (by showing probailities)
const STATUS_OPTIONS = Object.entries(STATUSES_WEIGHTS).map(
  ([status, weight]) => {
    // weight is a decimal (e.g. 0.1 → 10%)
    const pct = (weight * 100).toFixed(0);
    return {
      label: `${status} (${pct}%)`,
      value: status
    };
  }
);

const STATUSES = Object.keys(STATUSES_WEIGHTS);

const NEW_FORECAST_TYPES = [
  "Omitted",
  "Included"  
];

const FORECAST_TYPES_WEIGHTS: { [k: string]: number } = {
  "most likely": 0.75,
  B: 0.5,
  C: 0.25,
  D: 0.1,
};

const FORECAST_TYPES = Object.keys(FORECAST_TYPES_WEIGHTS);

interface Props {
  action?: string;//opp id
  target: string;
}

interface Form {
  practitioner: string;
  status: string;
  expectedCloseDate: string;
  annualEstimate?: string;
  projectedTotal?: number | null;
  winLossReason?: string;
  forecastType: string;
  products?: {
    id: number;
    quantity: number;
    annualQuantity: number;
  }[];
}

const OpportunityForm: React.FC<Props> = ({ action, target }) => {
  const isRisk = target === "risk";

  const [lastState, setLastState] = useState<Form>({
    practitioner: "",
    status: isRisk ? { label: "At Risk", value: "At Risk" } as any
      : null,
    expectedCloseDate: new Date().toISOString().substring(0, 7).replace(/-/g, "/"),
    annualEstimate: "",
    projectedTotal: null,
    winLossReason: "",
    forecastType: { label: "Omitted", value: "Omitted" } as any,
    products: [],
  });
  const [form, setForm] = useState<Form>({
    practitioner: "",
    status: isRisk ? { label: "At Risk", value: "At Risk" } as any
      : null,
    expectedCloseDate: new Date().toISOString().substring(0, 7).replace(/-/g, "/"),
    annualEstimate: "",
    projectedTotal: null,
    winLossReason: "",
    forecastType: { label: "Omitted", value: "Omitted" } as any,
    products: [],
  });
  const [formState, setFormState] = useState<Partial<Form>>({});
  const navigate = useNavigate();
  const [expectedProjection, setExpectedProjection] = useState(false);
  const [displayAddProductModal, setDisplayAddProductModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData, getData, patchData, deleteData } = useExternalApiService(apiUrl);
  const currentPract = store.currentPractitioner;
  const dateInputRef = useRef();
  const [isFrozen, setIsFrozen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);



  const isValid = Object.keys(formState).length === 0;

  // used to fix a weird behavior of the library we're using to handle select
  const getSelectCleanValue = (valueFromSelect: any) => {
    return valueFromSelect.value;
  }

  const isProjectedTotalVisible = isRisk || (((form.status as any)?.value === "Prospect (Sales Qualified)")
    || ((form.status as any)?.value === "Closed Lost"));

  const isClosedOptionSelected = isRisk
    ? (((form.status as any)?.value === "Lost Business")
      || ((form.status as any)?.value === "Retained"))
    : (((form.status as any)?.value === "Closed Lost")
      || ((form.status as any)?.value === "Closed Won"));

  const isCloseValid = isClosedOptionSelected && (form.winLossReason as any)?.value;

  const reasons = isClosedOptionSelected ? ((form?.status as any)?.value?.includes("Won")
    ? WIN_LOSS_REASONS.win
    : WIN_LOSS_REASONS.loss) : [];

  const oppName = form.products && form.products.filter((p) => p.id).length > 0 ?
    `${store.availableProducts.find((ap) => ap.id === form.products?.at(0)?.id)?.name} Opp` :
    isRisk ? "Risk" : "Opportunity";

  const projectedTotal = () => {
    if (expectedProjection) {
      const cleaned = form.annualEstimate?.replace(/[$,]/g, "") || "0";
      const monthsLeft = getNumberRemaingMonthsThisYear(form.expectedCloseDate + "/03");
      return monthsLeft * (parseFloat(cleaned) / 12);
    }
    return form.projectedTotal ?? 0;  
  
    /*if (!form.products || form.products.filter((p) => p.id).length === 0) {
      const expectedProjection = form?.projectedTotal?.replace(/[$,]/g, "") || "";
      return parseFloat(expectedProjection || "0");
    }
    return 0;*/

    // if (form.products && form.products.length > 0) {
    //   return form.products.reduce((acc, cur) => {
    //     const ap = store.availableProducts.find((ap) => ap.id === cur.id);
    //     return acc + (cur.quantity || 0) * (ap?.unit_price || 0) / 100;
    //   }, 0);
    // }
    // return 0;
  }

  const weightedTotal = ((isRisk ? RISK_STAGES : SALES_STAGES)
    ?.find((ss) => ss.label === (form.status as any)?.label)?.percentage || 0) * projectedTotal();

  const undo = () => {
    setForm(lastState);
  }

  /*
  const saveOpportunity = () => {
    if (isValid) {
      setIsSubmitting(true);
      const annualEstimate = form?.annualEstimate?.replace(/[$,]/g, "") || "";
      const remainingMonths = getNumberRemaingMonthsThisYear(
        form.expectedCloseDate.replace("-", "/") + "/03"
      );
      //const projectedTotal = remainingMonths * (parseFloat(annualEstimate) / 12);
      const projectedTotal = expectedProjection
        ? remainingMonths * (parseFloat(annualEstimate) / 12)
        : form.projectedTotal ?? 0;

      const payload: { [k: string]: any } = {
        practitioner_id: currentPract.id,
        status: getSelectCleanValue(form.status),
        forecast_type: getSelectCleanValue(form.forecastType),
        annual_estimate: parseFloat(annualEstimate) || 0,
        projected_total: projectedTotal,
        expected_close_date: form.expectedCloseDate.replace("-", "/") + "/01",
        win_loss_reason: getSelectCleanValue(form?.winLossReason) || "",
        is_risk: 0,
      }
      const productList = form.products?.filter((p) => p.quantity);
      if (productList && (productList?.length || 0) > 0) {
        payload["products"] = productList;
      }
      if (isRisk) {
        payload["is_risk"] = 1;
      }

      postData('/api/get_opportunities', payload).then((r) => {
        if (r.success) {
          window.history.back()
        }
      }).finally(() => setIsSubmitting(false));
    }
  };
  */

  useEffect(() => {
    if (form.annualEstimate) {
      const remainingMonths = getNumberRemaingMonthsThisYear(
        form.expectedCloseDate.replace("-", "/") + "/03"
      );
      const annualEstimate = form?.annualEstimate?.replace(/[$,]/g, "") || "";
      const projectedTotal = expectedProjection
        ? remainingMonths * (parseFloat(annualEstimate) / 12)
        : form.projectedTotal ?? 0;

      setForm((prev) => ({
        ...prev,
        projectedTotal: projectedTotal,
      }));
    }
  }, [form.annualEstimate]);

  const saveOpportunity = () => {
    if (isValid) {
      setIsSubmitting(true);
      const annualEstimate = form?.annualEstimate?.replace(/[$,]/g, "") || "";
      const remainingMonths = getNumberRemaingMonthsThisYear(
        form.expectedCloseDate.replace("-", "/") + "/03"
      );
      const projectedTotal = expectedProjection
        ? remainingMonths * (parseFloat(annualEstimate) / 12)
        : form.projectedTotal ?? 0;

      const payload: { [k: string]: any } = {
        practitioner_id: currentPract.id,
        status: getSelectCleanValue(form.status),
        forecast_type: getSelectCleanValue(form.forecastType),
        annual_estimate: parseFloat(annualEstimate) || 0,
        projected_total: projectedTotal,
        expected_close_date: form.expectedCloseDate.replace("-", "/") + "/01",
        win_loss_reason: getSelectCleanValue(form?.winLossReason) || "",
        is_risk: isRisk ? 1 : 0,
        no_products: expectedProjection ? 1 : 0, 
        practitioner_nsid: currentPract.netsuite_id,
      }

      const productList = form.products?.filter((p) => p.annualQuantity)
        ?.map((p) => ({
          ...p,
          quantity: remainingMonths * (p.annualQuantity / 12),
          annual_quantity: p.annualQuantity,
        }));

      if (productList && productList.length > 0) {
        payload["products"] = productList;
      }

      postData('/api/get_opportunities', payload).then((r) => {
        if (r.success) {
          window.history.back()
        }
      }).finally(() => setIsSubmitting(false));
    }
  };

  const deleteOpportunity = () => {
    if (isValid) {
      deleteData(`/api/get_opportunities/${action}`).then((r) => {
        if (r.success) {
          window.history.back()
        }
      })
    }
  };

  /*
  const editOpportunity = () => {
    if (isValid) {
      setIsSubmitting(true);
      const annualEstimate = form?.annualEstimate?.replace(/[$,]/g, "") || "";
      const remainingMonths = getNumberRemaingMonthsThisYear(
        form.expectedCloseDate.replace("-", "/") + "/03"
      );
      //const projectedTotal = remainingMonths * (parseFloat(annualEstimate) / 12);

      const projectedTotal = expectedProjection
        ? remainingMonths * (parseFloat(annualEstimate) / 12)
        : form.projectedTotal ?? 0;


      const payload: { [k: string]: any } = {
        practitioner_id: currentPract.id,
        status: getSelectCleanValue(form.status),
        forecast_type: getSelectCleanValue(form.forecastType),
        annual_estimate: parseFloat(annualEstimate) || 0,
        projected_total: projectedTotal || 0,
        expected_close_date: form.expectedCloseDate.replace("-", "/") + "/01",
        win_loss_reason: getSelectCleanValue(form?.winLossReason),
        is_risk: 0,
      }
      if (annualEstimate) {
        payload["products"] = [];
      }
      const productList = form.products?.filter((p) => p.quantity);
      if (productList && productList.length > 0) {
        payload["products"] = productList;
      }
      if (isRisk) {
        payload["is_risk"] = 1;
      }

      patchData(`/api/get_opportunities/${action}`, payload).then((r) => {
        if (r.success) {
          window.history.back()
        }
      }).finally(() => setIsSubmitting(false));
    }
  };
  */

   const editOpportunity = () => {
    if (isValid) {
      setIsSubmitting(true);
      const annualEstimate = form?.annualEstimate?.replace(/[$,]/g, "") || "";
      const remainingMonths = getNumberRemaingMonthsThisYear(
        form.expectedCloseDate.replace("-", "/") + "/03"
      );
      const projectedTotal = expectedProjection
        ? remainingMonths * (parseFloat(annualEstimate) / 12)
        : form.projectedTotal ?? 0;

      const payload: { [k: string]: any } = {
        practitioner_id: currentPract.id,
        status: getSelectCleanValue(form.status),
        forecast_type: getSelectCleanValue(form.forecastType),
        annual_estimate: parseFloat(annualEstimate) || 0,
        projected_total: projectedTotal || 0,
        expected_close_date: form.expectedCloseDate.replace("-", "/") + "/01",
        win_loss_reason: getSelectCleanValue(form?.winLossReason),
        is_risk: isRisk ? 1 : 0,
        no_products: expectedProjection ? 1 : 0, 
        practitioner_nsid: currentPract.netsuite_id,
      }
      const productList = form.products?.filter((p) => p.annualQuantity)
        ?.map((p) => ({
          ...p,
          quantity: remainingMonths * (p.annualQuantity / 12),
          annual_quantity: p.annualQuantity,
        }));

      if (productList && productList.length > 0) {
        payload["products"] = productList;
      } else {
        payload["products"] = [];
      }

      patchData(`/api/get_opportunities/${action}`, payload).then((r) => {
        if (r.success) {
          window.history.back()
        }
      }).finally(() => setIsSubmitting(false));
    }
  };

/*
  const closeOpportunity = () => {
    if (isValid) {
      setIsSubmitting(true);
      const annualEstimate = form?.annualEstimate?.replace(/[$,]/g, "") || "";
      const remainingMonths = getNumberRemaingMonthsThisYear(
        form.expectedCloseDate.replace("-", "/") + "/03"
      );
      //const projectedTotal = remainingMonths * (parseFloat(annualEstimate) / 12);
      const projectedTotal = expectedProjection
        ? remainingMonths * (parseFloat(annualEstimate) / 12)
        : form.projectedTotal ?? 0;

      const payload: { [k: string]: any } = {
        practitioner_id: currentPract.id,
        status: getSelectCleanValue(form.status),
        forecast_type: getSelectCleanValue(form.forecastType),
        annual_estimate: parseFloat(annualEstimate) || 0,
        projected_total: projectedTotal || 0,
        expected_close_date: form.expectedCloseDate.replace("-", "/") + "/01",
        win_loss_reason: getSelectCleanValue(form?.winLossReason),
        close_date: (new Date()).toISOString().substring(0, 10),
      }
      const productList = form.products?.filter((p) => p.quantity);
      if (productList && productList.length > 0) {
        payload["products"] = productList;
      }
      if (isRisk) {
        payload["is_risk"] = 1;
      }

      patchData(`/api/get_opportunities/${action}`, payload).then((r) => {
        if (r.success) {
          window.history.back()
        }
      }).finally(() => setIsSubmitting(false));
    }
  };

  */

  const closeOpportunity = () => {
    if (isValid) {
      setIsSubmitting(true);
      const annualEstimate = form?.annualEstimate?.replace(/[$,]/g, "") || "";
      const remainingMonths = getNumberRemaingMonthsThisYear(
        form.expectedCloseDate.replace("-", "/") + "/03"
      );
      const projectedTotal = expectedProjection
        ? remainingMonths * (parseFloat(annualEstimate) / 12)
        : form.projectedTotal ?? 0;

      const payload: { [k: string]: any } = {
        practitioner_id: currentPract.id,
        status: getSelectCleanValue(form.status),
        forecast_type: getSelectCleanValue(form.forecastType),
        annual_estimate: parseFloat(annualEstimate) || 0,
        projected_total: projectedTotal || 0,
        expected_close_date: form.expectedCloseDate.replace("-", "/") + "/01",
        win_loss_reason: getSelectCleanValue(form?.winLossReason),
        close_date: (new Date()).toISOString().substring(0, 10),
        is_risk: isRisk ? 1 : 0,
        no_products: expectedProjection ? 1 : 0, // ✅ send to backend
        practitioner_nsid: currentPract.netsuite_id,
      }

      const productList = form.products?.filter((p) => p.annualQuantity)
        ?.map((p) => ({
          ...p,
          quantity: remainingMonths * (p.annualQuantity / 12),
          annual_quantity: p.annualQuantity,
        }));

      if (productList && productList.length > 0) {
        payload["products"] = productList;
      } 

      patchData(`/api/get_opportunities/${action}`, payload).then((r) => {
        if (r.success) {
          window.history.back()
        }
      }).finally(() => setIsSubmitting(false));
    }
  };

  useEffect(() => {
    const errObj: { [k: string]: string } = {};

    const statusValue = (form.status as any)?.value;
    if (!statusValue) {
      errObj["status"] = "empty";
    }
    if (!form.expectedCloseDate) {
      errObj["expectedCloseDate"] = "empty";
    }
    // if (!form.forecastType) {
    //   errObj["forecastType"] = "empty";
    // }

    const annualEstimate = form.annualEstimate?.replace(/[$, ]/g, "") || "";
    if (!annualEstimate && (!form.products || form.products.length === 0)) {
      errObj["proj-products"] = "empty"
    }

    setFormState(errObj);
  }, [form]);


  /*
  useEffect(() => {
    setForm({
      ...form,
      practitioner: { value: store?.currentPractitioner?.id, label: store.currentPractitioner.name } as any,
    });

    if (action) {
      setEditMode(true);
      getData(`/api/get_opportunities/${action}`).then((data) => {
        const { opportunity } = data;
        const f = {
          expectedCloseDate: new Date(opportunity.expected_close_date).toISOString().substring(0, 7),
          status: { value: opportunity.status, label: opportunity.status } as any,
          forecastType: { value: opportunity.forecast_type, label: opportunity.forecast_type } as any,
          practitioner: opportunity.practitioner_id,
          annualEstimate: formatMoney(opportunity?.annual_estimate, true),
          //projectedTotal: opportunity?.expected_projection + "",  // I changed the backend so that it doesn't return this old name.  It's causing errors.
          projectedTotal: opportunity?.projected_total ?? null,
          products: opportunity?.products?.map((p: { id: number, quantity: number }) => ({
            id: p.id,
            quantity: p.quantity,
          })),
          winLossReason: { value: opportunity?.win_loss_reason, label: opportunity?.win_loss_reason } as any,
        };


        if (!opportunity.products || opportunity.products.length === 0) {
          setExpectedProjection(true);
        }        

        if (opportunity?.win_loss_reason && opportunity?.close_date) {
          setIsFrozen(true);
        }

        setForm(f);
        setLastState(f);
        setFormState({});
      })
    }
  }, [action]);
  */

  useEffect(() => {
    setForm({
      ...form,
      practitioner: { value: store?.currentPractitioner?.id, label: store.currentPractitioner.name } as any,
    });

    if (action) {
      setEditMode(true);
      getData(`/api/get_opportunities/${action}`).then((data) => {
        const { opportunity } = data;
        const f = {
          expectedCloseDate: new Date(opportunity.expected_close_date).toISOString().substring(0, 7).replace(/-/g, "/"),
          status: { value: opportunity.status, label: opportunity.status } as any,
          forecastType: { value: opportunity.forecast_type, label: opportunity.forecast_type } as any,
          practitioner: opportunity.practitioner_id,
          annualEstimate: formatMoney(opportunity?.annual_estimate, true),
          projectedTotal: opportunity?.projected_total ?? null,
          products: opportunity?.products?.map((p: { id: number, quantity: number, annual_quantity: number }) => ({
            id: p.id,
            quantity: p.quantity,
            annualQuantity: p.annual_quantity,
          })),
          winLossReason: { value: opportunity?.win_loss_reason, label: opportunity?.win_loss_reason } as any,
        };

        if (opportunity?.products 
          && opportunity?.products.filter((p: any) => p.quantity).length > 0
        ) {
          setExpectedProjection(false);
        } else {
          setExpectedProjection(true);
        }

        if (opportunity?.win_loss_reason && opportunity?.close_date) {
          setIsFrozen(true);
        }

        setForm(f);
        setLastState(f);
        setFormState({});
      })
    }
  }, [action]);

  useEffect(() => {
    if (!isProjectedTotalVisible) {
      setForm({
        ...form,
        annualEstimate: "",
        projectedTotal: null,
      });
      setExpectedProjection(false);
    }

  }, [isProjectedTotalVisible]);

  useEffect(() => {
    if (isClosedOptionSelected) {
      setForm({
        ...form,
        winLossReason: "",
      });
    }
  }, [isClosedOptionSelected]);

  if (displayAddProductModal) {
    return (
      <>
        <ProductForm
          onCancel={() => setDisplayAddProductModal(false)}
          onAddProduct={(productId) => {
            const newProduct = {
              id: productId,
              annualQuantity: 10,
              quantity: 0,
            };

            setForm({
              ...form,
              products: form.products ? [
                ...form.products,
                newProduct
              ] : [newProduct]
            })

            setDisplayAddProductModal(false);
          }}
          availableProducts={store.availableProducts} />
      </>
    )
  }

  return (
    <>
      <BackIcon
        onClick={() => {
          // Go back in browser history to return to baseball card
          window.history.back();
        }}
        className={styles["back-icon"]} />
      <div className={styles["container"]}>

        {editMode ? (
          <div className={isFrozen ? styles["margin-bottom"] : ""}>
            <div className={styles["form-header-edit"]}>
              <h3>
                {/*oppName*/}
                {isRisk ? "Risk" : "Opportunity"}
              </h3>
              <div>
                <h3>
                  {/*formatMoney(projectedTotal() + "", true)*/}
                   {form.projectedTotal == null
                    ? ""
                    : formatMoney(String(form.projectedTotal), true)}
                </h3>
              </div>
            </div>
            {!isFrozen && (
              <div className={styles["form-subheader"]}>
                <div></div>
                <div
                  className={styles["loading-button"]}
                  onClick={() => {
                    if (!isSubmitting) {
                      editOpportunity();
                    }
                  }}>
                  {isSubmitting && (
                    <>
                      <span className={styles["loading-button__loader"]}></span>
                      <span className={styles["loading-button__loader-cover"]}></span>
                    </>
                  )}
                  <button
                    disabled={!isValid}
                    className={styles["draft-button"]}
                    type="button">
                    SAVE
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className={styles["form-header"]}>
            <h3>{isRisk ? "Risk" : "Opportunity"} for</h3>
            <div
              className={styles["loading-button"]}
              onClick={() => {
                if (!isSubmitting) {
                  saveOpportunity();
                }
              }}>
              {isSubmitting && (
                <>
                  <span className={styles["loading-button__loader"]}></span>
                  <span className={styles["loading-button__loader-cover"]}></span>
                </>
              )}
              <button
                disabled={!isValid}
                className={styles["draft-button"]}
                type="button">
                SAVE
              </button>
            </div>
          </div>
        )}


        <form className={styles["form"]}>
          {!editMode && (
            <Title order={3} fw="normal" mb="md">
              {currentPract.primary_name}
            </Title>
          )}
          <div className={styles["form__input"]}>
            <label style={{ display: "flex", alignItems: "center" }}>
              {isRisk ? "Risk Stage" : "Sales Stage"}
              {isFrozen && (<LockIcon style={{ marginLeft: 5, marginTop: -5 }} />)}
            </label>
            <Select
              value={form.status}
              isDisabled={isFrozen}
              onChange={(v: any) => setForm({ ...form, status: v })}
              placeholder="Select Sales Stage"
              defaultValue="Sales Ready"
              components={{
                Control: (e) => {
                  return (
                    <Group
                      bg={isFrozen ? "#F2F2F2" : "auto"}
                      c={isFrozen ? "#999999" : "auto"}
                      align="center"
                      onClick={() => {
                        if (e.selectProps.menuIsOpen) {
                          e.selectProps.onMenuClose();
                        } else {
                          e.selectProps.onMenuOpen();
                        }
                      }}>
                      {e.children}
                    </Group>
                  )
                },
                SingleValue: (e) => {
                  const containerRef = useRef();

                  const fullOptionObj = (isRisk ? RISK_STAGES : SALES_STAGES)
                    .find((s) => s.label === (e.data as any).value);

                  const handleClickOutside = (event: any) => {
                    if (containerRef.current && !(containerRef.current as HTMLElement).contains(event.target)) {
                      e.selectOption(e.data);
                    }
                  }

                  useEffect(() => {
                    document.addEventListener('mousedown', handleClickOutside);
                    return () => {
                      document.removeEventListener('mousedown', handleClickOutside);
                    };
                  }, [])

                  if (!fullOptionObj) return <></>;
                  const percentageFormatted =
                    `${(fullOptionObj.percentage * 100).toFixed(0)}%`;

                  ;
                  return (
                    <Box
                      w="100%"
                      mt={0}
                      ref={containerRef as any}>
                      <Title order={5} w="100%" c="dark">
                        {fullOptionObj.label}: {percentageFormatted}
                      </Title>
                      <Title order={6} w="100%" fw="normal" c="gray">
                        {fullOptionObj.description}
                      </Title>
                    </Box>
                  )
                },
                Input: (e) => {
                  return <></>;
                },
                MenuList: (o) => {
                  return (
                    <ScrollArea h={300}>
                      {o.children}
                    </ScrollArea>
                  )
                },
                Option: (o) => {
                  const fullOptionObj = (isRisk ? RISK_STAGES : SALES_STAGES)
                    .find((s) => s.label === (o.data as any).value);
                  if (!fullOptionObj) return <></>;
                  const percentageFormatted =
                    `${(fullOptionObj.percentage * 100).toFixed(0)}%`;

                  return (
                    <Box
                      w="95%"
                      my={5}
                      style={{
                        borderBottom: "1px solid #eee",
                        padding: "10px",
                      }}
                      bg={(o.data as any)?.value === (form.status as any)?.value ? "#E3F4FF" : "auto"}
                      onClick={() => o.selectOption(o.data)}>
                      <Title order={5} w="100%" c="dark" mb={2}>
                        {fullOptionObj.label}: {percentageFormatted}
                      </Title>
                      <Title order={6} w="100%" fw="normal" c="gray">
                        {fullOptionObj.description}
                      </Title>
                    </Box>
                  );
                }
              }}
              styles={{
                control: (baseStyles: any) => ({
                  ...baseStyles,
                  border: 'none',
                  borderBottom: '1px solid #ccc',
                  paddingLeft: 0,
                  marginLeft: 0,
                  color: '#4F7D9C',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: 'none'
                  }
                }),
                valueContainer: (baseStyles: any) => ({
                  ...baseStyles,
                  paddingLeft: 0,
                  marginLeft: 0,
                  color: '#4F7D9C',
                }),
                indicatorSeparator: () => ({
                  display: 'none'
                }),
                menu: (baseStyles: any) => ({
                  ...baseStyles,
                  zIndex: 999,
                }),
              }}
              /*options={STATUSES.map((p) => ({
                label: p,
                value: p,
              })) as []} 
               */
              options={(isRisk ? RISK_STAGES : SALES_STAGES).filter((sw) => editMode || !sw.label.includes("Closed")).map((sw) => ({
                label: sw.label,
                value: sw.label,
              })) as []}
            />
          </div>
          {isClosedOptionSelected && (
            <div className={styles["form__input"]}>
              <label >
                Win/Loss Reason
                {isFrozen && (<LockIcon style={{ marginLeft: 5, marginTop: -5 }} />)}
              </label>
              <Select
                value={form.winLossReason}
                isDisabled={isFrozen}
                onChange={(v: any) => setForm({ ...form, winLossReason: v })}
                placeholder="Win/Loss Reason"
                styles={{
                  control: (baseStyles: any) => ({
                    ...baseStyles,
                    border: 'none',
                    borderBottom: '1px solid #ccc',
                    paddingLeft: 0,
                    marginLeft: 0,
                    color: '#4F7D9C',
                    boxShadow: 'none',
                    '&:hover': {
                      boxShadow: 'none'
                    }
                  }),
                  valueContainer: (baseStyles: any) => ({
                    ...baseStyles,
                    paddingLeft: 0,
                    marginLeft: 0,
                    color: '#4F7D9C',
                  }),
                  indicatorSeparator: () => ({
                    display: 'none'
                  }),
                  menu: (baseStyles: any) => ({
                    ...baseStyles,
                    zIndex: 999,
                  }),
                }}
                options={reasons.map((r) => ({
                  label: r,
                  value: r,
                })) as []} />
            </div>
          )}
          <Box pos="relative">
            <div className={styles["form__input"]}>
              <label style={{ display: "flex", alignItems: "center" }}>
                {/*Expected Close Date*/}
                Revenue {isRisk && "Risks"} Start Date
                {isFrozen && (<LockIcon style={{ marginLeft: 5, marginTop: -5 }} />)}
              </label>
              <DatePicker
                selected={new Date(form.expectedCloseDate + "/01")}
                onChange={(d) => {                  
                  if (d) {
                    setForm({
                      ...form,
                      expectedCloseDate: d?.toISOString().substring(0,7).replace(/-/g, "/") || "",
                    })
                  }                  
                }}
                className={styles["custom-datepicker"]}
                showMonthYearPicker
                dateFormat="MMMM d, YYYY"
              />

              {/* <input
                ref={dateInputRef as any}
                type="month"
                style={{
                  opacity: 0,
                  position: "absolute",
                  pointerEvents: "auto"
                }}
                value={form.expectedCloseDate}
                onChange={(e: any) => {
                  if (e.target.value) {
                    setForm({ ...form, expectedCloseDate: e.target.value });
                  }
                }} />
              <Title
                order={4}
                fw="normal"
                pos="relative"
                py={3}
                bg={isFrozen ? "#F2F2F2" : "auto"}
                c={isFrozen ? "#999999" : "auto"}
                style={{
                  borderBottom: "1px solid lightgray",
                }}
                onClick={() => {
                  if (!isFrozen && dateInputRef.current) {
                    (dateInputRef.current as HTMLDivElement).click();
                    (dateInputRef.current as HTMLInputElement).showPicker();
                  }
                }}>
                {getFullDateUSStyle(new Date(form.expectedCloseDate + "/01"))}
              </Title> */}
            </div>
            {/* <div className={styles["form__input"]}>
            <label>
              Forecast Type
            </label>
            <Select
              value={form.forecastType}
              onChange={(v: any) => setForm({ ...form, forecastType: v })}
              placeholder="Forecast Type"
              styles={{
                control: (baseStyles: any) => ({
                  ...baseStyles,
                  border: 'none',
                  borderBottom: '1px solid #ccc',
                  paddingLeft: 0,
                  marginLeft: 0,
                  color: '#4F7D9C',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: 'none'
                  }
                }),
                valueContainer: (baseStyles: any) => ({
                  ...baseStyles,
                  paddingLeft: 0,
                  marginLeft: 0,
                  color: '#4F7D9C',
                }),
                indicatorSeparator: () => ({
                  display: 'none'
                })
              }}
              options={NEW_FORECAST_TYPES.map((p) => ({
                label: p,
                value: p,
              })) as []} />
          </div> */}

            <div className={styles["rev-label"]}>
              Annual Revenue {isRisk && "Risk"} Estimate
            </div>
            {isProjectedTotalVisible && (
              <div className={styles["form-checkbox"]}>

                <input type="checkbox"
                  checked={expectedProjection}
                  onChange={() => {
                    if (isFrozen) {
                      return;
                    }
                    setExpectedProjection(!expectedProjection);
                    setForm({
                      ...form,
                      annualEstimate: "",
                      products: [],
                    })
                  }}
                />
                <span style={{ display: "flex", alignItems: "center", fontSize: "12pt"}}>
                  Unsure what products to add yet
                  {isFrozen && (<LockIcon style={{ marginLeft: 5, marginTop: -5 }} />)}
                </span>
              </div>
            )}
            {expectedProjection ? (
              <div className={styles["form__input-proj"]}>
                <div className={styles["rev-bold-title"]}>
                  Enter revenue {isRisk && "risk"} estimate for an entire year
                </div>
                <input
                  style={{
                    backgroundColor: isFrozen ? "#F2F2F2" : "auto",
                    color: isFrozen ? "#999999" : "auto",
                  }}
                  value={form.annualEstimate}
                  onChange={(e) => {
                    if (isFrozen) {
                      return;
                    }
                    const v = formatMoney(e.target.value, true);
                    setForm({ ...form, annualEstimate: v })
                  }}
                  placeholder="$10,000"
                  disabled={isFrozen}
                  type="text" />
              </div>
            ) : (
              <div className={styles["product-list-container"]}>

                <ProductList
                  disabled={isFrozen}
                  products={form.products
                    ?.filter((p) => p.id)
                    ?.map((p) => {
                      const ap = store.availableProducts.find((ap) => ap.id === p.id);
                      return {
                        id: p.id,
                        quantity: p.annualQuantity,
                        sku: ap?.sku || "unavailable",
                        name: ap?.name || "unavailable",
                        unit_price: ap?.unit_price || 0,
                      }
                    })}
                  onRemove={(productId) => {
                    if (isFrozen) {
                      return;
                    }
                    setForm({
                      ...form,
                      products: form.products?.filter((p) =>
                        p.id !== productId
                      )
                    });
                  }}
                  onEditQuantity={(productId, newQuantity) => {
                    if (isFrozen) {
                      return;
                    }
                    setForm({
                      ...form,
                      products: form.products?.map((p) => {
                        if (p.id === productId) {
                          return {
                            ...p,
                            annualQuantity: newQuantity
                          }
                        }
                        return p;
                      })
                    });
                  }}
                />
                
                <button
                  type="button"
                  onClick={() => !isFrozen && setDisplayAddProductModal(true)}
                  className={styles["form__add-product-btn"]}
                  style={{ backgroundColor: isFrozen ? "#bbbbbb" : "auto" }}>
                  {isFrozen ? (
                    <LockIcon style={{ padding:"10px", stroke: "white"}} />
                  ):(
                    "ADD PRODUCTS"
                  )}
                </button>
              </div>
            )}
            {!isFrozen && isClosedOptionSelected && (
              <Overlay
                backgroundOpacity={0.7}
                color="#fff">
              </Overlay>
            )}
          </Box>
        </form>
        {editMode && (
          <div>
            <div className={styles["dataview"]}>
              <Title order={4} c="gray" my="md">
                Forecast (from Netsuite)
                {isFrozen && (<LockIcon style={{ marginLeft: 5, marginTop: -5 }} />)}
              </Title>
              {//form.products && form.products.filter((p) => p.quantity).length > 0  // this is wrong
               form.products && form.products.filter((p) => p.quantity).length > 0 && form.projectedTotal == null              
                ? (
                  <div className={styles["opp-list__item-awaiting"]}>
                    Awaiting Netsuite Data
                  </div>
                ) : (
                  <Group justify="center" my="md">
                    <Stack gap={0} align="center">
                      <Title                     
                        c={isFrozen ? "#999999" : "auto"}
                        order={1} 
                        fw="normal">
                        {formatMoney(String(form.projectedTotal), true)}
                      </Title>
                      <Title order={6} fw="normal" c="gray" fz="xs" ta="center">
                        Projected Total Revenue {isRisk && "Risk"}  <br />
                        Until Year End
                      </Title>
                    </Stack>
                    <Stack gap={0} align="center">
                      <Title                     
                        c={isFrozen ? "#999999" : "auto"}
                        order={1} 
                        fw="normal">
                        {formatMoney(weightedTotal + "", true)}
                      </Title>
                      <Title order={6} fw="normal" c="gray" fz="xs" ta="center">
                        Weighted Total Revenue {isRisk && "Risk"}  <br />
                        Based On Sales Stage
                      </Title>
                    </Stack>
                  </Group>
                )
              }
              
            </div>
            {/*<Button
              loading={isSubmitting}
              w="100%"
              ml={-20}
              bg={`${isFrozen || !isCloseValid ? "auto" : "#4f7d9c"}`}
              disabled={isFrozen || !isCloseValid}
              onClick={() => closeOpportunity()}>
              CLOSE OPPORTUNITY
            </Button>*/}
              {isFrozen ? (
                <Group justify="center" my="md">
                  <Title order={3} c="#99999" my="md">You've already closed this {isRisk ? "risk" : "opportunity"}</Title>
                </Group>                
              ) : (
                <Button
                  loading={isCloseValid && !isFrozen && isSubmitting}
                  w="100%"
                  ml={-20}
                  bg={`${isFrozen || !isCloseValid ? "auto" : "#4f7d9c"}`}
                  disabled={isFrozen || !isCloseValid}
                  onClick={() => closeOpportunity()}>
                  CLOSE {isRisk ? "RISK" : "OPPORTUNITY"}
                </Button>
              )}
          </div>
        )}
      </div>
    </>
  );
};

export default OpportunityForm;
